/**
 * Shapes for `api/giftcards/*`.
 *
 * Taken from the API source rather than the swagger copy — `GiftCardDto`,
 * `GiftCardTransactionDto`, `GiftCardPurchaseReportObj` and the two enums in
 * `Gopanza.Api.Services.Abstractions/Models`.
 */

/** `GiftCardSearchType`. Declaration order is the wire value. */
export const SEARCH_TYPE = {
  all: 0,
  activated: 1,
  activatedWithBalance: 2,
  activatedNoBalance: 3,
  nonActivated: 4,
} as const;

export type SearchType = (typeof SEARCH_TYPE)[keyof typeof SEARCH_TYPE];

/** `GiftCardTransactionType`. */
export const TRANSACTION_TYPE = { load: 0, redemption: 1 } as const;

export interface CardOwner {
  id: string;
  firstName: string | null;
  lastName: string | null;
  completeName?: string | null;
  email: string | null;
  phoneNumber?: string | null;
  avatar?: string | null;
}

/** One row of the Cards tab. */
export interface GiftCard {
  id: string;
  createdAt: string;
  /** Null until someone loads money onto it at a register. */
  activatedOn: string | null;
  code: string | null;
  /** What is left to spend — the number that matters. */
  currentBalanceLeft: number;
  balance: number;
  openingBalance: number;
  nickname: string | null;
  owner: CardOwner | null;
}

/** `GiftCardLiteDto` — what `giftcards/balance/{code}` answers with. */
export interface GiftCardLite {
  id: string;
  activatedOn: string | null;
  code: string | null;
  nickname: string | null;
  currentBalanceLeft: number;
  owner: CardOwner | null;
}

/** One row of the Transactions tab. Carries its card, so the tab needs no join. */
export interface GiftCardTransaction {
  id: string;
  createdAt: string;
  giftCardId: string | null;
  giftCardCode: string | null;
  giftCardNickname: string | null;
  giftCardCurrentBalance: number;
  orderId: string | null;
  orderConfirmationCode: string | null;
  orderBusinessName: string | null;
  transactionType: number;
  amount: number;
}

/** One transaction of a single card — the same row without the card's own fields. */
export interface CardTransaction {
  id: string;
  createdAt: string;
  giftCardId: string | null;
  giftCardCurrentBalance: number;
  orderId: string | null;
  orderConfirmationCode: string | null;
  orderBusinessName: string | null;
  transactionType: number;
  amount: number;
}

/** One row of the Purchases tab. Loads only — money on, never off. */
export interface PurchaseRow {
  id: string;
  giftCardId: string | null;
  date: string;
  code: string | null;
  amount: number;
  confirmationCode: string | null;
  storeName: string | null;
  giftCardCurrentBalance: number;
}

/**
 * `Purchased` and `Redeemed` ride along on three different responses, and only
 * two of them mean anything.
 *
 * `GetGiftCardsHistoryAsync` and `GetGiftCardHistoryAsync` build them from the
 * same query their rows come from, so there they describe exactly the period
 * (or the card) on screen. `SearchGiftCardsAsync` does not: it sums every
 * transaction in the date window and applies neither the text query nor the
 * activation filter, so searching one card's code still returns platform-wide
 * figures. The Cards tab does not show them for that reason — and, having no
 * date range, would only ever receive all-time totals anyway.
 */
export interface PeriodTotals {
  purchased: number;
  redeemed: number;
}

export interface GiftCardSearchResult extends PeriodTotals {
  total: number;
  giftCards: GiftCard[];
}

export interface TransactionsResult extends PeriodTotals {
  total: number;
  transactions: GiftCardTransaction[];
}

export interface CardHistoryResult extends PeriodTotals {
  total: number;
  transactions: CardTransaction[];
}

export interface PurchaseReport {
  total: number;
  transactions: PurchaseRow[];
}

/** Outstanding liability — what the business still owes in unspent balances. */
export interface AccountPayable {
  totalActiveGiftCards: number;
  accountPayable: number;
}

export function cardLabel(card: {
  code: string | null;
  nickname: string | null;
}): string {
  return card.nickname?.trim() || card.code || '—';
}

export function ownerName(owner: CardOwner | null): string {
  if (!owner) return '';
  return (
    owner.completeName?.trim() ||
    [owner.firstName, owner.lastName].filter(Boolean).join(' ')
  );
}
